import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { AlertTriangle, Check, Info, X } from 'lucide-react';
import clsx from 'clsx';
import { tx } from '../i18n';

type Kind = 'success' | 'error' | 'info';
/** A button inside the toast — "Undo" after removing something is what it is for. */
export interface ToastAction {
  label: string;
  onClick: () => void;
}
interface Toast {
  id: number;
  kind: Kind;
  message: string;
  action?: ToastAction;
}

type Push = (message: string, kind?: Kind, options?: { action?: ToastAction }) => void;
const ToastContext = createContext<{ push: Push } | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback<Push>((message, kind = 'info', options = {}) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, kind, message, action: options.action }]);
    // a toast that offers an action stays longer: reading it and deciding takes time
    const ms = kind === 'error' ? 8000 : options.action ? 8000 : 4000;
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), ms);
  }, []);

  const value = useMemo(() => ({ push }), [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="pointer-events-none fixed bottom-4 right-4 z-100 flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2"
        role="status"
        aria-live="polite"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            className={clsx(
              'card pointer-events-auto flex items-start gap-2.5 px-3 py-2.5 text-sm shadow-lg',
              t.kind === 'error' && 'border-l-4 border-l-[var(--color-danger-500)]',
              t.kind === 'success' && 'border-l-4 border-l-[var(--color-teal-500)]',
              t.kind === 'info' && 'border-l-4 border-l-[var(--color-accent-600)]',
            )}
          >
            <span className="mt-0.5 shrink-0">
              {t.kind === 'error' ? (
                <AlertTriangle size={16} className="text-[var(--color-danger-500)]" />
              ) : t.kind === 'success' ? (
                <Check size={16} className="text-[var(--color-teal-500)]" />
              ) : (
                <Info size={16} className="text-[var(--color-accent-600)]" />
              )}
            </span>
            <p className="min-w-0 flex-1 whitespace-pre-wrap break-words">{t.message}</p>
            {t.action && (
              <button
                className="btn btn-sm btn-outline -my-0.5 shrink-0"
                onClick={() => {
                  t.action!.onClick();
                  setToasts((all) => all.filter((x) => x.id !== t.id));
                }}
              >
                {t.action.label}
              </button>
            )}
            <button
              className="btn btn-icon btn-ghost -mr-1 -mt-1"
              onClick={() => setToasts((all) => all.filter((x) => x.id !== t.id))}
              aria-label={tx('Dismiss')}
            >
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast outside ToastProvider');
  return ctx;
}
